/**
 * useBatchRecords Hook
 *
 * Custom hook for fetching individual records within a batch
 */

import { useState, useEffect, useCallback } from 'react';
import { bulkMisAPI } from '../../../../services/bulk-mis-api.service';
import type {
  BulkIndividualRecord,
  BulkMisPagination,
  BulkBatchRecord,
  BulkBatchDetailsSummary,
  BulkBatchMetadata,
} from '../../../../types/bulk-mis';

interface UseBatchRecordsReturn {
  data: {
    batch_info: BulkBatchRecord | null;
    records: BulkIndividualRecord[];
    pagination: BulkMisPagination;
    summary: BulkBatchDetailsSummary | null;
    metadata: BulkBatchMetadata | null;
  } | null;
  loading: boolean;
  error: string | null;
  fetchRecords: () => void;
}

/**
 * Hook to fetch records for a specific batch by reference_id
 */
export const useBatchRecords = (
  referenceId: string,
  page: number = 1,
  limit: number = 25,
  status: string = 'all',
  search: string = '',
  includeBalances: boolean = false,
  bulkAction: string = 'data_fetch'
): UseBatchRecordsReturn => {
  const [data, setData] = useState<UseBatchRecordsReturn['data']>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!referenceId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await bulkMisAPI.getBatchDetails({
        reference_id: referenceId,
        page,
        limit,
        status: status !== 'all' ? (status as any) : undefined,
        search: search && search.trim().length > 0 ? search : undefined,
        include_balances: includeBalances,
        bulk_action: bulkAction,
      });

      setData({
        batch_info: null, // Will be populated from parent component
        records: response.data.records,
        pagination: response.data.pagination,
        summary: response.data.summary,
        metadata: response.data.metadata,
      });
    } catch (err) {
      console.error('[useBatchRecords] Error fetching records:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch batch records');
    } finally {
      setLoading(false);
    }
  }, [referenceId, page, limit, status, search, includeBalances, bulkAction]);

  // Clear any data from the previously-open batch the moment the user switches batches.
  // Without this, the drawer briefly renders the previous batch's summary cards / records
  // until the new batch's API response arrives — visible especially when the new batch
  // is large (long fetch) and the previous batch was small (instant fetch).
  // Note: this only fires on referenceId change, NOT on pagination/filter changes within
  // the same batch — keeping stale data during in-batch param changes avoids flicker.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [referenceId]);

  useEffect(() => {
    if (referenceId) {
      fetchRecords();
    }
  }, [fetchRecords]);

  return { data, loading, error, fetchRecords };
};
